import { yupResolver } from "@hookform/resolvers/yup";
import {
    BuildChangeFundsRequest,
    setChangeFundsRequestSuccessful,
    setStepByName,
    StepperIndexes,
    stepperStateSelector,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import i18n from "i18next";
import { FC, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { DefaultStepProps, PensionQuestionnaireStepProps, TriggerArrowRight, useTypedTranslation } from "../..";
import { VerificationInputs } from "../VerificationInputs";
import * as Styled from "./SMSVerificationPage.styled";

const NUMBER_OF_INPUTS = 8;

const schema = yup.object({
    verificationCode: yup
        .array()
        .of(yup.string().required())
        .required()
        .test("is-array", i18n.t("investmentStrategy.errorMessages.verificationCode"), (val) => Array.isArray(val))
        .test(
            "length",
            i18n.t("investmentStrategy.errorMessages.eightNumbers"),
            (val) => Array.isArray(val) && val.length === NUMBER_OF_INPUTS,
        )
        .test(
            "all-filled",
            i18n.t("investmentStrategy.errorMessages.verificationCode"),
            (val) => Array.isArray(val) && val.every((ch) => (ch ?? "").trim() !== ""),
        )
        .test(
            "only-digits",
            i18n.t("investmentStrategy.errorMessages.onlyDigits"),
            (val) => Array.isArray(val) && val.every((ch) => /^\d$/.test(ch ?? "")),
        ),
});

type FormValues = { verificationCode: string[] };

export const SMSVerificationPage: FC<PensionQuestionnaireStepProps & DefaultStepProps> = ({
    queries,
    setContinueCondition: stepperContinueCondition,
    contractId,
}) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const { sendInvestmentStrategyRequestMutation, precontractualDocsQuery, questionnaireConfigQuery } = queries;
    const { mutateAsync: sendInvestmentStrategyMutateAsync } = sendInvestmentStrategyRequestMutation!;
    const { data: precontractualDocsData, refetch: refetchPrecontractualDocs } = precontractualDocsQuery!;
    const { data: questionnaireConfigData, refetch: refetchQuestionnaireConfig } = questionnaireConfigQuery!;
    const stepperState = useAppSelector(stepperStateSelector);

    useEffect(() => {
        if (!precontractualDocsData) refetchPrecontractualDocs?.();
    }, [precontractualDocsData, refetchPrecontractualDocs]);

    useEffect(() => {
        if (!questionnaireConfigData) refetchQuestionnaireConfig?.();
    }, [questionnaireConfigData, refetchQuestionnaireConfig]);

    const handleSmsSending = () => dispatch(setStepByName(StepperIndexes.phoneNumberPage)); //Go back to phone number selection step

    const methods = useForm<FormValues>({
        defaultValues: {
            verificationCode: Array.from({ length: NUMBER_OF_INPUTS }, () => ""),
        },
        resolver: yupResolver(schema),
    });
    const { setError, handleSubmit } = methods;

    const validateAndSubmit = useCallback(async () => {
        if (!questionnaireConfigData || !precontractualDocsData?.name) return false;

        let canContinue = false;

        await handleSubmit(
            // onValid form
            async (data) => {
                const smsCode = data.verificationCode.join("");
                const preparedMutationRequest = BuildChangeFundsRequest(
                    stepperState,
                    questionnaireConfigData,
                    precontractualDocsData.name,
                    smsCode,
                );

                try {
                    if (!preparedMutationRequest || !sendInvestmentStrategyMutateAsync || !contractId) {
                        throw new Error(t("investmentStrategy.errorMessages.missingData"));
                    }

                    await sendInvestmentStrategyMutateAsync?.({
                        contractId: String(contractId),
                        data: preparedMutationRequest,
                    });
                    dispatch(setChangeFundsRequestSuccessful(true));
                    canContinue = true;
                } catch (e) {
                    const raw: any = e;
                    const code = raw?.response?.data?.code;
                    if (code === 800) {
                        // SMS code not valid, stay on the same page and display error message
                        setError("verificationCode", {
                            type: "server",
                            message: t("investmentStrategy.errorMessages.smsCodeNotValid"),
                        });
                        canContinue = false;
                    } else {
                        // Unexpected error - should go to next page with generic error message
                        dispatch(setChangeFundsRequestSuccessful(false));
                        canContinue = true;
                    }
                }
            },
            // onInvalid form
            () => {
                canContinue = false;
            },
        )();

        return canContinue;
    }, [
        handleSubmit,
        stepperState,
        questionnaireConfigData,
        precontractualDocsData,
        sendInvestmentStrategyMutateAsync,
        contractId,
        dispatch,
        setError,
        t,
    ]);

    useEffect(() => {
        stepperContinueCondition(validateAndSubmit);
    }, [stepperContinueCondition, validateAndSubmit]);

    return (
        <FormProvider {...methods}>
            <form>
                <Styled.Wrapper>
                    <Styled.Title>{t("investmentStrategy.sms.main")}</Styled.Title>
                    <Styled.Paragraph>{t("investmentStrategy.sms.enterCode")}</Styled.Paragraph>
                    <Styled.Label>{t("investmentStrategy.sms.code")}</Styled.Label>
                    <VerificationInputs numberOfInputs={NUMBER_OF_INPUTS} name="verificationCode" />
                    <TriggerArrowRight onClick={handleSmsSending}>
                        {t("investmentStrategy.sms.newCode")}
                    </TriggerArrowRight>
                </Styled.Wrapper>
            </form>
        </FormProvider>
    );
};
